/**
 * Servicio para interactuar con el API del catálogo de vehículos
 * Sistema simplificado para acceso directo a la base de datos local
 * Proporciona métodos para obtener marcas, modelos y buscar vehículos
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * Obtener todas las marcas disponibles
 * @returns {Promise<Object>} - Lista de marcas
 */
export const getMarcas = async () => {
  try {
    //console.log('🚗 [CatalogoVehiculosService] Obteniendo marcas...');
    
    const response = await apiHelpers.generic.get(
      API_ENDPOINTS.CATALOGO_VEHICULOS?.MARCAS || '/api/catalogo-vehiculos/marcas',
      'marcas'
    );
    
    if (response?.data?.success) {
      //console.log('✅ [CatalogoVehiculosService] Marcas obtenidas:', response.data.total || 0);
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0
      };
    }
    
    throw new Error('Respuesta inválida del servidor');
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error en getMarcas:', error);
    
    // Si es error 503, el catálogo está deshabilitado
    if (error.message.includes('503') || error.message.includes('deshabilitado')) {
      //console.log('🔧 [CatalogoVehiculosService] Catálogo deshabilitado, usando modo manual');
    }
    
    return {
      success: false,
      error: error.message || 'Error al obtener marcas',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener modelos por marca
 * @param {number} marcaId - ID de la marca
 * @returns {Promise<Object>} - Lista de modelos
 */
export const getModelos = async (marcaId) => {
  try {
    if (!marcaId) {
      throw new Error('ID de marca es requerido');
    }

    //console.log('🚗 [CatalogoVehiculosService] Obteniendo modelos para marca:', marcaId);
    
    const endpoint = API_ENDPOINTS.CATALOGO_VEHICULOS?.MODELOS || 
      `/api/catalogo-vehiculos/marcas/${marcaId}/modelos`;
    
    const response = await apiHelpers.generic.get(
      typeof endpoint === 'function' ? endpoint(marcaId) : endpoint.replace(':marcaId', marcaId),
      'modelos'
    );
    
    if (response?.data?.success) {
      //console.log('✅ [CatalogoVehiculosService] Modelos obtenidos:', response.data.total || 0);
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        marca_id: response.data.marca_id || marcaId
      };
    }
    
    throw new Error('Respuesta inválida del servidor');
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error en getModelos:', error);
    
    return {
      success: false,
      error: error.message || 'Error al obtener modelos',
      data: [],
      marca_id: marcaId,
      total: 0
    };
  }
};

/**
 * Buscar vehículos por texto (marcas y modelos)
 * @param {string} searchText - Texto a buscar
 * @returns {Promise<Object>} - Resultados de búsqueda
 */
export const searchVehiculos = async (searchText) => {
  try {
    if (!searchText || searchText.trim().length < 2) {
      return {
        success: true,
        results: { marcas: [], modelos: [] },
        total: 0,
        searchText: searchText
      };
    }

    //console.log('🔍 [CatalogoVehiculosService] Buscando vehículos:', searchText);
    
    const endpoint = API_ENDPOINTS.CATALOGO_VEHICULOS?.SEARCH || 
      '/api/catalogo-vehiculos/search';
    
    const response = await apiHelpers.generic.get(endpoint, 'busqueda', {
      params: { q: searchText.trim() }
    });
    
    if (response?.data?.success) {
      //console.log('✅ [CatalogoVehiculosService] Búsqueda completada:', response.data.total || 0, 'resultados');
      return {
        success: true,
        results: response.data.results || { marcas: [], modelos: [] },
        total: response.data.total || 0,
        searchText: response.data.searchText || searchText
      };
    }
    
    throw new Error('Respuesta inválida del servidor');
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error en searchVehiculos:', error);
    
    return {
      success: false,
      error: error.message || 'Error en búsqueda de vehículos',
      results: { marcas: [], modelos: [] },
      searchText: searchText,
      total: 0
    };
  }
};

/**
 * Obtener estadísticas del catálogo
 * @returns {Promise<Object>} - Estadísticas del catálogo
 */
export const getStats = async () => {
  try {
    //console.log('📊 [CatalogoVehiculosService] Obteniendo estadísticas...');
    
    const endpoint = API_ENDPOINTS.CATALOGO_VEHICULOS?.STATS || 
      '/api/catalogo-vehiculos/stats';
    
    const response = await apiHelpers.generic.get(endpoint, 'estadisticas');
    
    if (response?.data?.success) {
      //console.log('✅ [CatalogoVehiculosService] Estadísticas obtenidas');
      return {
        success: true,
        stats: response.data.stats || {
          enabled: false,
          total_marcas: 0,
          total_modelos: 0,
          total_versiones: 0,
          ultima_sync: null,
          necesita_sync: false
        }
      };
    }
    
    throw new Error('Respuesta inválida del servidor');
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error en getStats:', error);
    
    return {
      success: false,
      error: error.message || 'Error al obtener estadísticas',
      stats: {
        enabled: false,
        total_marcas: 0,
        total_modelos: 0,
        total_versiones: 0,
        ultima_sync: null,
        necesita_sync: false
      }
    };
  }
};

/**
 * Verificar si el catálogo está disponible
 * @returns {Promise<boolean>} - True si está disponible
 */
export const verificarDisponibilidad = async () => {
  try {
    //console.log('🔍 [CatalogoVehiculosService] Verificando disponibilidad...');
    
    const result = await getStats();
    const disponible = result.success && result.stats?.enabled;
    
    //console.log('✅ [CatalogoVehiculosService] Disponibilidad verificada:', disponible);
    return disponible;
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error verificando disponibilidad:', error);
    return false;
  }
};

/**
 * Crear opciones para dropdown a partir de datos del catálogo
 * @param {Array} data - Datos del catálogo
 * @param {string} valueField - Campo para el value (default: 'id')
 * @param {string} labelField - Campo para el label (default: 'nombre')
 * @returns {Array} - Opciones formateadas para dropdown
 */
export const crearOpcionesDropdown = (data, valueField = 'id', labelField = 'nombre') => {
  if (!Array.isArray(data)) {
    console.warn('⚠️ [CatalogoVehiculosService] crearOpcionesDropdown recibió datos no válidos:', data);
    return [];
  }
  
  return data.map(item => ({
    id: item[valueField],
    value: item[valueField],
    label: item[labelField] || item.nombre || `Item ${item[valueField]}`,
    data: item // Datos completos para referencia
  }));
};

/**
 * Obtener marcas con transformación para dropdown
 * @returns {Promise<Array>} - Opciones de marcas para dropdown
 */
export const getMarcasDropdown = async () => {
  try {
    const result = await getMarcas();
    
    if (result.success) {
      return crearOpcionesDropdown(result.data);
    }
    
    return [];
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error en getMarcasDropdown:', error);
    return [];
  }
};

/**
 * Obtener modelos con transformación para dropdown
 * @param {number} marcaId - ID de la marca
 * @returns {Promise<Array>} - Opciones de modelos para dropdown
 */
export const getModelosDropdown = async (marcaId) => {
  try {
    if (!marcaId) {
      return [];
    }
    
    const result = await getModelos(marcaId);
    
    if (result.success) {
      return crearOpcionesDropdown(result.data);
    }
    
    return [];
    
  } catch (error) {
    console.error('❌ [CatalogoVehiculosService] Error en getModelosDropdown:', error);
    return [];
  }
};

/**
 * Funciones de compatibilidad con el código existente
 */

/**
 * Obtener marcas (compatibilidad con clase)
 * @returns {Promise<Object>} - Lista de marcas
 */
export const obtenerMarcas = getMarcas;

/**
 * Obtener modelos (compatibilidad con clase)
 * @param {number} marcaId - ID de la marca
 * @returns {Promise<Object>} - Lista de modelos
 */
export const obtenerModelos = getModelos;

/**
 * Buscar vehículos (compatibilidad con clase)
 * @param {string} searchText - Texto a buscar
 * @returns {Promise<Object>} - Resultados de búsqueda
 */
export const buscarVehiculos = searchVehiculos;

/**
 * Obtener estadísticas (compatibilidad con clase)
 * @returns {Promise<Object>} - Estadísticas del catálogo
 */
export const obtenerStats = getStats;

// Exportar servicio como objeto para compatibilidad
export const catalogoVehiculosApiService = {
  getMarcas,
  getModelos,
  searchVehiculos,
  getStats,
  verificarDisponibilidad,
  crearOpcionesDropdown,
  getMarcasDropdown,
  getModelosDropdown
};

// Export default para compatibilidad
export default catalogoVehiculosApiService;